import React, { useEffect, useState } from 'react';
import { usePosting, useTitle } from '../../Hooks';
import { PostingJob, PostingUser, PostingUserStatus, SetLoading } from '../../Classes';
import { useParams, useLoaderData } from 'react-router-dom';
import { Button, Header, Placeholder, Table, Message, Input, Toast, Stat, Label } from 'react-frontier';
import { Card, Modal } from '../../components';
import { bindClick } from '@tempu/components/Util';
import { firstBy } from 'thenby';
import API, { CDN_URL } from '../../API';
import BusinessAccess from '../../BusinessAccess';
import moment from 'moment';

enum Modals{
	INFO = 1,
	APPROVE = 2,
	REJECT = 3,
}

var PostingApplicants= ()=>{
   var params = useParams<{ pid: string }>();
   var posting_id = parseInt(params?.pid);
	var { hasAccess, setPendingUsers } = usePosting(posting_id);
   var { setTitle, setAction } = useTitle();
   var [loadError, setLoadError] = useState<string>(null);
   var [users, setUsers] = useState<PostingUser[]>(null);
	var [selectedUser, setSelectedUser] = useState<PostingUser>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [rejectMessage, setRejectMessage] = useState<string>(null);
	var [jobs, setJobs] = useState<PostingJob []>(useLoaderData() as PostingJob [])

	useEffect(()=>{
		setTitle('Aplicantes');
      if(!params || !posting_id || Number.isNaN(posting_id)) return;
		if(!hasAccess(BusinessAccess.USERS.APPROVE_USERS)) return;

		setAction(
			<div>
				<Button icon color='black' size='big' iconName='file-download'  onClick={downloadApplicants}/>
			</div>
		);

		API.getPostingUsers(posting_id, PostingUserStatus.PENDING, true).then(res=>{
			if(res.error) setLoadError(res.message);
			setUsers(res.data);
		}).catch(err=>{
			setLoadError('Hubo un error cargando la lista de aplicantes (LCL-1)');
		})
	}, []);

	var openApplicantModal = (user: PostingUser)=>{
		setSelectedUser(user);
		setShownModal(Modals.INFO);
	};

	var downloadApplicants = ()=>{
		API.downloadPostingApplicants(posting_id).then(res=>{		
		}).catch(err=>{
			setLoadError('Hubo un error inesperado descargando los empleados (LCL-1)');
		});
	}

	var approveUser = (setLoading: SetLoading) => {
		if(!selectedUser) return setModalPrompts(['Debes seleccionar un aplicante.']);
		setLoading(true);

		API.approveJobUser(posting_id, selectedUser.job_id, selectedUser.user_id).then(res => {
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha aprobado el usuario aplicante!');

			var job = jobs.find(j => j.job_id === selectedUser.job_id);
			var isJobFull = job && (job.job_users + 1) >= job.job_count;

			setUsers(users.map(user => {
				if(user.user_id === selectedUser.user_id && user.job_id === selectedUser.job_id) {
					return { ...user, approved: true, rejected: false };
				}
				if(isJobFull && user.job_id === selectedUser.job_id && !user.approved && !user.rejected) {
					return { ...user, limit_available: false };
				}
				return user;
			}));

			setJobs(jobs.map(job => 
				job.job_id === selectedUser.job_id 
					? { ...job, job_users: job.job_users + 1 } 
					: job
			));

			setPendingUsers(users.filter(u => !u.approved && !u.rejected).length);
			setShownModal(null);
		}).catch(() => {
			setModalPrompts(['Hubo un error al aprobar el aplicante. (AWE-1)']);
		}).finally(() => {
			setLoading(false);
		});
	};

	var rejectUser = async (setLoading: SetLoading)=>{
		if(!selectedUser) return setModalPrompts(['Debes seleccionar un aplicante.']);
		setLoading(true);
		API.rejectJobUser(posting_id, selectedUser.job_id, selectedUser.user_id, rejectMessage).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha rechazado el usuario aplicante');

			var ux = users.findIndex(a=>a.user_id===selectedUser.user_id && a.job_id===selectedUser.job_id);
			if(ux>-1){
				var nus = [...users];
				nus[ux].approved = false;
				nus[ux].rejected = true;
				setPendingUsers(nus.reduce((a,b)=>a+((!b.approved && !b.rejected) ? 1 : 0),0))
				setUsers(nus);
			}
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al rechazar el aplicante. (RWE-2)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	if(!hasAccess(BusinessAccess.USERS.APPROVE_USERS)){
		return <Header text='Sin acceso' subtext='No tienes acceso a esta página' />
	}
	
	return <div className="tp posting-applicants-screen">
		<div className="fr stats block">
			<Stat label={'Aplicantes'} value={users ? (
				users.length
			) : (
				<Placeholder width={40} height={30} />
			)} />
		</div>
		{loadError ? (
			<Header text='Error' subtext={loadError} />
		) : users ? (
			Object.entries(
				users
					.sort(firstBy('job_id', 'asc').thenBy('rejected', 'asc').thenBy('date_created', 'asc'))
					.reduce((i, user) => { (i[user.job_name] ||= []).push(user); return i }, {} as Record<string, typeof users>)
			).map(([jobName, groupedUsers]) => (
				<React.Fragment key={`JOB-${jobName}`}>
					<Header text={jobName} centered={false} style={{ marginTop: 15 }} size='small' />
					{groupedUsers.map(a => (
						<Card
							className='applicant'
							image={a.image}
							imageFallbackIcon='image'
							key={`PSTATS-${a.user_id}`}
							onClick={() => openApplicantModal(a)}
						>
							<div className="left">
								<div className="name">{a.first_name} {a.last_name}</div>
								<div className="job-name">{a.job_name}</div>
								<div className="meta">Aplicó: {moment.unix(a.date_created).format('DD/MMM/YY HH:mm')}</div>
							</div>
							<div className="right">
								<Label color={
									a.rejected ? 'red' :
									a.approved ? 'green' :
									!a.limit_available ? 'orange' : 'blue'
								}>
									{a.rejected ? 'Rechazado' :
									a.approved ? 'Aprobado' :
									!a.limit_available ? 'Puesto lleno' : 'Pendiente'}
								</Label>
							</div>
						</Card>
					))}
				</React.Fragment>
			))
		) : (
			Array.from({ length: 3 }).map((_, i) => (
				<Card placeholder placeholderImage className='applicant' key={`CRDPLH-${i}`}>
					<div className="left">
						<Placeholder height={24} width={200} />
						<Placeholder width={50} />
						<Placeholder width={50} />
					</div>
				</Card>
			))
		)}
		{users && users.length === 0 && (
			<Header text={'Sin aplicantes'} subtext={'Este empleo no tiene aplicantes pendientes'} iconName='user-plus' />
		)}
		<Modal open={shownModal === Modals.INFO} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Información de aplicante</Modal.Header>
			{selectedUser && (
				<Modal.Content>
					<Table details striped fitted>
					<Table.Row data={['Nombre', `${selectedUser.first_name} ${selectedUser.last_name}`]} />
					<Table.Row data={['Puesto', selectedUser.job_name]} />
					<Table.Row data={['Correo electrónico', selectedUser.email]} />
					<Table.Row data={['Teléfono', selectedUser.phone]} />
					<Table.Row data={['Aplicó', moment.unix(selectedUser.date_created).format('DD/MMM/YY HH:mm')]} />
					{selectedUser.worked ? (
						<Table.Row data={['Estatus', <Label size='small' color='green'>Empleado anteriormente</Label>]} />
					) : null }
					{selectedUser.rejected ? (
						<>
							<Table.Row data={['Rechazó', selectedUser.user_rejected_name]} />
							<Table.Row data={['Fecha de rechazo', moment.unix(selectedUser.date_rejected).format('DD/MMM/YY HH:mm')]} />
						</>
					) : null }
					</Table>
					{!selectedUser.rejected && !selectedUser.approved && (
					<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px' }}>
						{!selectedUser.limit_available && <Message type='warning' text='No se pueden aprobar más aplicantes para este puesto porque ya no hay vacantes disponibles.' /> }
						<div style={{ display: 'flex', width: '100%', gap: '10px' }}>
							{selectedUser.limit_available ? (
								<Button
									color='green'
									size='small'
									text='Aprobar'
									style={{ flex: 1 }}
									onClick={() => setShownModal(Modals.APPROVE)}
								/>
							) : null }					
							<Button
								basic
								color='red'
								size='small'
								text='Rechazar'
								style={{ flex: 1 }}
								onClick={() => setShownModal(Modals.REJECT)}
							/>
						</div>
					</div>
					)}
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text='Cerrar' onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
		{hasAccess(BusinessAccess.USERS.APPROVE_USERS) && (
			<Modal open={shownModal===Modals.APPROVE && !!selectedUser} onClose={bindClick(setShownModal)} size='mini'>
				<Modal.Header>Aprobar aplicante</Modal.Header>
				{!!selectedUser && <Modal.Content>
					<Header text='¿Aprobar aplicante?' subtext={`${selectedUser.first_name} ${selectedUser.last_name || ''} - ${selectedUser.job_name}`} style={{ marginBottom: 15 }} />
					<Message type='info' centered>
						Esto <b>aprobará</b> al aplicante <b>{selectedUser.first_name} {selectedUser.last_name}</b> para el puesto <b>{selectedUser.job_name}</b> y se le mandará un correo electrónico de aprobación
					</Message>
					<Message list={modalPrompts} type='error' />
				</Modal.Content>}
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Aprobar' color='green' onClick={approveUser}/>
				</Modal.Actions>
			</Modal>
		)}
		{hasAccess(BusinessAccess.USERS.APPROVE_USERS) && (
			<Modal open={shownModal===Modals.REJECT && !!selectedUser} onClose={bindClick(setShownModal)} size='mini'>
				<Modal.Header>Rechazar aplicante</Modal.Header>
				{!!selectedUser && <Modal.Content>
					<Header text='¿Rechazar aplicante?' subtext={`${selectedUser.first_name} ${selectedUser.last_name || ''} - ${selectedUser.job_name}`} style={{ marginBottom: 15 }} />
					<Message type='info' centered>
						Esto <b>rechazará</b> al aplicante <b>{selectedUser.first_name} {selectedUser.last_name}</b> para el puesto <b>{selectedUser.job_name}</b>.
						<br />
						Podrás poner un mensaje de rechazo para el usuario
					</Message>
					<Input label='Motivo de rechazo' value={rejectMessage} onChange={setRejectMessage} style={{ marginTop: 15 }} textarea />
					<Message list={modalPrompts} type='error' />
				</Modal.Content>}
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Rechazar' color='red' onClick={rejectUser}/>
				</Modal.Actions>
			</Modal>
		)}
	</div>
}

export default PostingApplicants;